import React, { useEffect, useState } from 'react';
import { CalendarClock, CalendarOff, Plus, Trash2 } from 'lucide-react';
import { CT, soft } from '@parsina/framework/tokens';
import {
  Alert, Button, ConfirmDialog, EmptyState, NumberInput, Skeleton, SkeletonList, SkeletonRegion, SkeletonText,
  Spinner, Toggle, useToast,
} from '@parsina/framework';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { SectionCard } from './SettingsKit';
import DayOffDialog from './DayOffDialog';
import { useOnlineBookingSettings, useDaysOff } from '../useOnlineBooking';
import { useOnlineBookingStrings } from '../onlineBookingStrings';
import {
  dayOffHoursLabel, dayOffRangeLabel, horizonDraftOf, horizonStatus, isHorizonDirty, parseHorizonDraft,
} from '../onlineBooking.shared';
import { userErrorMessage } from '../../../lib/userErrorMessage';

// ─────────────────────────────────────────────────────────────────────────────
// «تنظیمات ▸ نوبت‌دهی آنلاین» on a workstation (PM-0382 §4.6; DESKTOP_WEB.md).
//
// Archetype D (Settings): one SectionCard per clinic where the doctor's
// membership is accepted, stacked (P1). Each card answers, in order:
//   ② why nothing may show online even though the switch is on — no presence
//     hours here, or the clinic takes no online bookings at all (max two alerts);
//   «بیماران بتوانند خودشان آنلاین نوبت بگیرند» — saved on the spot;
//   «تا [ N ] روز آینده» — typed, saved with Enter or «ذخیره», the clinic's cap
//     under it and, below, what patients can actually book now;
//   «روزهای مرخصی» — the upcoming ones, «افزودن روز مرخصی», each removable after
//     a confirmation.
//
// Desktop only for now: the phone's Settings does not list this section
// (./marketSections.js). App-local component (DESIGN_CONCEPT §17.4): it embeds
// this app's data hooks, so it is not a kit candidate.
// ─────────────────────────────────────────────────────────────────────────────

const STACK = '1.125rem';

/** The panel's own loading shape: a card with a switch row, a field and a list. */
function OnlineBookingSkeleton({ label }) {
  return (
    <SkeletonRegion label={label} data-testid="settings-booking" style={{ display: 'flex', flexDirection: 'column', gap: STACK, maxWidth: '58.75rem' }}>
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{
            background: CT.card, border: `1px solid ${CT.border}`, borderRadius: '0.875rem',
            padding: '1.25rem 1.375rem', display: 'flex', flexDirection: 'column', gap: '1rem',
          }}
        >
          <SkeletonText width="40%" height={16} />
          <SkeletonText lines={2} />
          <Skeleton width="14rem" height={44} />
          <SkeletonList count={1} avatar={false} trailing={72} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

export default function OnlineBookingPanel({ onGoToClinics }) {
  const strings = useOnlineBookingStrings();
  const { t } = strings;
  const s = useOnlineBookingSettings();

  if (s.status === 'loading') return <OnlineBookingSkeleton label={t('common.state.loading')} />;

  if (s.status === 'error') {
    return (
      <div data-testid="settings-booking" data-state="error" style={{ maxWidth: '58.75rem' }}>
        <ErrorState title={t('settings.onlineBooking.loadFailed')} error={s.error} onRetry={s.retry} />
      </div>
    );
  }

  if (s.status === 'empty') {
    return (
      <div data-testid="settings-booking" data-state="empty" style={{ maxWidth: '58.75rem' }}>
        <EmptyState
          icon={<CalendarClock size={44} strokeWidth={1.5} aria-hidden />}
          title={t('settings.onlineBooking.empty.title')}
          description={t('settings.onlineBooking.empty.hint')}
          action={onGoToClinics && (
            <Button variant="outline" onClick={onGoToClinics} data-testid="settings-booking-go-clinics">
              {t('settings.onlineBooking.empty.action')}
            </Button>
          )}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="settings-booking"
      data-state="content"
      role="region"
      aria-label={t('settings.onlineBooking.regionLabel')}
      style={{ display: 'flex', flexDirection: 'column', gap: STACK, maxWidth: '58.75rem' }}
    >
      {s.settings.map((setting) => (
        <ClinicBookingCard
          key={setting.clinicId}
          setting={setting}
          strings={strings}
          pausing={setting.clinicId in s.pausing}
          savingHorizon={Boolean(s.savingHorizon[setting.clinicId])}
          onPause={s.setPaused}
          onSaveHorizon={s.saveHorizon}
        />
      ))}
    </div>
  );
}

function ClinicBookingCard({ setting, strings, pausing, savingHorizon, onPause, onSaveHorizon }) {
  const { t } = strings;
  const { formatNumber } = useTranslation();
  const toast = useToast();
  const id = setting.clinicId;
  const testid = `settings-booking-clinic-${id}`;
  const clinicName = setting.clinicName || t('settings.onlineBooking.unnamedClinic');

  const [draft, setDraft] = useState(() => horizonDraftOf(setting));
  const [fieldError, setFieldError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const inputId = `${testid}-horizon`;

  // The stored value moved (a save landed, or the switch paused the horizon):
  // the field shows it, and an unsaved draft does not survive a pause.
  useEffect(() => {
    setDraft(horizonDraftOf(setting));
    setFieldError(null);
    setSaveError(null);
  }, [setting.horizonDays, setting.paused]);

  const dirty = isHorizonDirty(draft, setting);
  const status = horizonStatus(setting);

  const toggle = async (allow) => {
    const res = await onPause(id, !allow);
    // A background save reports by toast (§7); the switch has already gone back.
    if (!res.ok) toast.error(userErrorMessage(res.error, strings, 'settings.onlineBooking.saveFailed'));
  };

  const reset = () => { setDraft(horizonDraftOf(setting)); setFieldError(null); setSaveError(null); };

  const save = async () => {
    // Enter on an unchanged field is not a save.
    if (savingHorizon || !dirty) return;
    const parsed = parseHorizonDraft(draft);
    if (parsed.error) { setFieldError(parsed.error); document.getElementById(inputId)?.focus?.(); return; }
    setFieldError(null);
    setSaveError(null);
    const res = await onSaveHorizon(id, parsed.value);
    if (res.ok) { toast.success(t('settings.onlineBooking.saved')); return; }
    // The field keeps every keystroke (§2.4); a refusal about the number lands on it.
    if (res.error?.extensions?.messageKey === 'errors.bff.onlineBooking.horizonInvalid') {
      setFieldError('settings.onlineBooking.horizon.invalid');
    } else {
      setSaveError(userErrorMessage(res.error, strings, 'settings.onlineBooking.saveFailed'));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    else if (e.key === 'Escape' && dirty) { e.preventDefault(); reset(); }
  };

  return (
    <SectionCard
      testId={testid}
      icon={<CalendarClock size={20} strokeWidth={1.75} aria-hidden />}
      iconBg={soft(CT.navy, 0.09)}
      iconColor={CT.navy}
      title={<bdi>{clinicName}</bdi>}
      subtitle={t('settings.onlineBooking.cardSubtitle')}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {setting.hasPresence === false && (
          <Alert variant="warning" title={t('settings.onlineBooking.warnings.noPresence')} data-testid={`${testid}-no-presence`}>
            {t('settings.onlineBooking.warnings.noPresenceHint')}
          </Alert>
        )}
        {setting.clinicAcceptsOnline === false && (
          <Alert variant="neutral" title={t('settings.onlineBooking.warnings.clinicClosed')} data-testid={`${testid}-clinic-closed`}>
            {t('settings.onlineBooking.warnings.clinicClosedHint')}
          </Alert>
        )}
      </div>

      <Toggle
        id={`${testid}-online`}
        data-testid={`${testid}-online`}
        label={t('settings.onlineBooking.allow.label')}
        description={t('settings.onlineBooking.allow.impact')}
        checked={!setting.paused}
        disabled={pausing}
        onChange={toggle}
      />

      <div
        style={{
          marginTop: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap',
          opacity: setting.paused ? 0.55 : 1,
        }}
      >
        <div style={{ width: '15rem', maxWidth: '100%' }}>
          <NumberInput
            id={inputId}
            data-testid={inputId}
            label={t('settings.onlineBooking.horizon.label')}
            value={draft}
            onChange={(e) => {
              setDraft(e?.target?.value ?? '');
              if (fieldError) setFieldError(null);
              setSaveError(null);
            }}
            onKeyDown={onKeyDown}
            unit={t('settings.onlineBooking.horizon.unit')}
            placeholder={t('settings.onlineBooking.horizon.placeholder')}
            // The rule is ours (1–365 or empty); the kit's own blur check is off.
            validate={() => ''}
            disabled={setting.paused || savingHorizon}
            error={fieldError ? t(fieldError) : undefined}
            helperText={t('settings.onlineBooking.horizon.clinicCap', { days: formatNumber(setting.clinicHorizonDays) })}
          />
        </div>
        {dirty && !setting.paused && (
          <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '1.75rem' }}>
            <Button onClick={save} disabled={savingHorizon} aria-busy={savingHorizon || undefined} data-testid={`${testid}-horizon-save`}>
              {savingHorizon && <Spinner size={16} color="currentColor" />}
              {t('common.actions.save')}
            </Button>
            <Button variant="outline" onClick={reset} disabled={savingHorizon} data-testid={`${testid}-horizon-reset`}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        )}
      </div>

      {status && (
        <p
          role="status"
          aria-live="polite"
          data-testid={`${testid}-effective`}
          style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: CT.textSecondary, lineHeight: 1.8 }}
        >
          {t(status.key, status.vars ? { days: formatNumber(status.vars.days) } : undefined)}
        </p>
      )}

      {saveError && (
        <Alert variant="error" data-testid={`${testid}-horizon-error`} style={{ marginTop: '0.75rem' }}>
          {saveError}
        </Alert>
      )}

      <DaysOffSection setting={setting} clinicName={clinicName} strings={strings} testid={testid} />
    </SectionCard>
  );
}

function DaysOffSection({ setting, clinicName, strings, testid }) {
  const { t } = strings;
  const { formatDate, formatDigits } = useTranslation();
  const toast = useToast();
  const d = useDaysOff(setting.clinicId, { from: setting.today });
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const region = `${testid}-daysoff`;
  const headingId = `${region}-title`;
  const fmt = { t, formatDate, formatDigits };

  const remove = async () => {
    const target = confirming;
    if (!target) return;
    const res = await d.remove(target.id);
    setConfirming(null);
    if (res.ok) toast.success(t('settings.onlineBooking.daysOff.removed'));
    else toast.error(userErrorMessage(res.error, strings, 'settings.onlineBooking.daysOff.removeFailed'));
  };

  let body;
  if (d.status === 'loading') {
    body = <SkeletonList count={2} avatar={false} trailing={72} label={t('common.state.loading')} />;
  } else if (d.status === 'error') {
    body = <ErrorState title={t('settings.onlineBooking.daysOff.loadFailed')} error={d.error} onRetry={d.retry} />;
  } else if (d.status === 'empty') {
    body = (
      <EmptyState
        icon={<CalendarOff size={28} strokeWidth={1.5} aria-hidden />}
        title={t('settings.onlineBooking.daysOff.empty')}
        description={t('settings.onlineBooking.daysOff.emptyHint')}
      />
    );
  } else {
    body = (
      <ul
        aria-labelledby={headingId}
        style={{
          margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem',
          opacity: d.refreshing ? 0.45 : 1, transition: 'opacity .2s',
        }}
      >
        {d.daysOff.map((dayOff) => {
          const range = dayOffRangeLabel(dayOff, fmt);
          return (
            <li
              key={dayOff.id}
              data-testid={`settings-booking-dayoff-${dayOff.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
                border: `1px solid ${CT.border}`, borderRadius: '0.625rem', padding: '0.625rem 0.875rem',
              }}
            >
              <div style={{ flex: 1, minWidth: '12rem' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: CT.textPrimary }}>{range}</div>
                <div style={{ fontSize: '0.75rem', color: CT.textSecondary, marginTop: '0.125rem' }}>
                  {dayOffHoursLabel(dayOff, fmt)}
                  {dayOff.reason && <> · <bdi dir="auto">{dayOff.reason}</bdi></>}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(dayOff)}
                disabled={Boolean(d.removing[dayOff.id])}
                aria-label={t('settings.onlineBooking.daysOff.removeAria', { range })}
                data-testid={`settings-booking-dayoff-${dayOff.id}-remove`}
              >
                <Trash2 size={16} aria-hidden />
                {t('settings.onlineBooking.daysOff.remove')}
              </Button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section
      aria-labelledby={headingId}
      style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: `1px solid ${CT.border}` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h3 id={headingId} style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: CT.textPrimary, flex: 1 }}>
          {t('settings.onlineBooking.daysOff.title')}
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAdding(true)}
          data-testid={`${region}-add`}
        >
          <Plus size={16} aria-hidden />
          {t('settings.onlineBooking.daysOff.add')}
        </Button>
      </div>
      <p style={{ margin: '0.375rem 0 0.875rem', fontSize: '0.75rem', color: CT.textMuted, lineHeight: 1.8 }}>
        {t('settings.onlineBooking.daysOff.hint')}
      </p>
      <div data-testid={region} data-state={d.status} aria-busy={d.refreshing || undefined}>
        {body}
      </div>

      <DayOffDialog
        open={adding}
        onClose={() => setAdding(false)}
        clinicId={setting.clinicId}
        clinicName={clinicName}
        today={setting.today}
        onSubmit={d.add}
        strings={strings}
        testid="settings-booking-dayoff-dialog"
      />

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => { if (!open && !d.removing[confirming?.id]) setConfirming(null); }}
        title={t('settings.onlineBooking.daysOff.removeTitle')}
        description={confirming
          ? t('settings.onlineBooking.daysOff.removeBody', { range: dayOffRangeLabel(confirming, fmt), clinic: clinicName })
          : ''}
        confirmLabel={t('settings.onlineBooking.daysOff.removeConfirm')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        loading={Boolean(confirming && d.removing[confirming.id])}
        onConfirm={remove}
        data-testid="settings-booking-dayoff-remove-dialog"
        confirmTestId="settings-booking-dayoff-remove-confirm"
        cancelTestId="settings-booking-dayoff-remove-cancel"
      />
    </section>
  );
}
